import { searchContext } from '../../../../context/Context';
import dropdownLists from '../../DropdownLists';
import { renderMappingButton } from '../renders/buttonRenderers';
import { filterTypes } from '../../SetupTablesResources';
import { getGeneNCBIid } from '../renders/MappingFunctions';
import { renderRoundedValues, renderListsFromObjects } from '../renders/CommonAtributes';
import { ButtonInfoGeneContent, OpenPopup } from '../components/index';
import { EXT_NCBI_GENE_LINK } from '../config/constants';
import { getHeaderProps } from '../config/helpers';
import { createSelectFilter, createCapitalizeFilter } from '../filters/filterConfigs';
import { renderGeneNCBILink, renderEnsemblLinks, renderUniProtLinks } from '../renders/linkRenderers';
import { renderDefaultValueCols } from '@/utils/renderDefaultValueCols';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';
import { isArray } from 'lodash';

function getGeneMappingId(data: any): string | number | undefined {
  return (
    data?.geneID ??
    data?.ncbiID ??
    data?.geneNcbiID ??
    data?.gene_ncbi_id ??
    (Array.isArray(data?.geneNcbiIDs) ? data.geneNcbiIDs[0] : data?.geneNcbiIDs)
  );
}

function getGeneMappingName(data: any): string | undefined {
  return data?.symbol ?? data?.geneSymbol;
}

function getValidGeneInfoId(data: any): string | undefined {
  const rawId = getGeneMappingId(data);
  if (rawId == null) return undefined;
  const normalized = String(rawId).trim();
  if (!normalized || normalized === '-') return undefined;
  return normalized;
}

export const getGeneColumns = context => {
  const { tab } = context;
  const headerProps = getHeaderProps();

  return [
    {
      name: 'symbol',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Symbol">Gene</span>,
      uniqueID: 'Gene',
      headerProps,
      columnSize: null,
      minWidth: 200,
      render: ({ data }) => {
        const { tab } = searchContext();

        const symbolResult = renderTextColumn(data?.symbol);
        const ncbiResult = renderTextColumn(data?.ncbiID);

        if (!symbolResult) return '-';

        let ids: any[] = [];
        let values: any[] = [];
        let geneInfo;
        let isGene = false;
        const delimiter = '|';

        if (tab === 'GENES') {
          isGene = true;
          ids = [ncbiResult];
          values = [symbolResult];
          const resolvedGeneId = getValidGeneInfoId(data);
          geneInfo = resolvedGeneId ? { symbol: symbolResult, id: resolvedGeneId } : undefined;
        }

        const formattedLinks = ids.map((id, index) => (
          <span key={id}>
            <a href={`${EXT_NCBI_GENE_LINK}${id}`} target="_blank" rel="noopener noreferrer">
              {values[index]}
              <i className="fa fa-external-link px-1" aria-hidden="true"></i>
            </a>
            {index !== ids.length - 1 && <span className="px-1">{delimiter}</span>}
          </span>
        ));

        return (
          <span className="geneModalLink">
            {formattedLinks}
            {isGene && <ButtonInfoGeneContent value={geneInfo} />}
          </span>
        );
      },
    },

    {
      name: 'geneSymbol',
      category: 'Gene',
      sortable: true,
      filterType: filterTypes.stringWithContains,
      header: <span title="Gene Symbol">Gene</span>,
      headerProps,
      minWidth: 200,
      render: ({ data }) => {
        const { tab } = searchContext();
        const delimiter = '|';

        const symbolResult = renderTextColumn(
          isArray(data?.geneSymbol) ? data?.geneSymbol[0] : data?.geneSymbol,
        );
        const ncbiResult = renderTextColumn(
          isArray(data?.geneNcbiID) ? data?.geneNcbiID[0] : data?.geneNcbiID,
        );

        let ids: any[] = [];
        let values: any[] = [];
        let geneInfo;

        if (tab === 'GDA' || tab === 'DISEASES' || tab === 'GENES') {
          ids = [ncbiResult];
          values = [symbolResult];
          const resolvedGeneId = getValidGeneInfoId(data);
          geneInfo = resolvedGeneId ? { symbol: symbolResult, id: resolvedGeneId } : undefined;
        } else if (tab === 'DDA') {
          const idsResult = renderDefaultValueCols(data.geneNcbiID ? [data.geneNcbiID] : data.geneNcbiIDs);

          if (idsResult === '-') return '-';

          ids = idsResult;
          values = [symbolResult];
          geneInfo = { symbol: symbolResult, id: idsResult };
        } else if (tab === 'VDA') {
          const idsResult = renderDefaultValueCols(data.geneNcbiIDs);
          const valuesResult = renderDefaultValueCols(data.geneSymbol);

          if (idsResult === '-' || valuesResult === '-') return '-';

          ids = idsResult;
          values = valuesResult;
        } else if (tab === 'VARIANTS') {
          const variantGenes = renderDefaultValueCols(data.variantToGenes);

          if (variantGenes === '-') return '-';

          ids = variantGenes.map(el => el.geneNcbiID);
          values = variantGenes.map(el => el.geneSymbol);
        }

        if (!ids.length) return '-';

        const formattedLinks = ids.map((id, index) => (
          <span key={`${id}-${index}`}>
            <a href={`${EXT_NCBI_GENE_LINK}${id}`} target="_blank" rel="noopener noreferrer">
              {values[index]}
              <i className="fa fa-external-link px-1" aria-hidden="true"></i>
            </a>
            {index !== ids.length - 1 && <span className="px-1">{delimiter}</span>}
          </span>
        ));

        return (
          <span className="geneModalLink">
            {formattedLinks}
            {tab === 'GDA' && geneInfo && <ButtonInfoGeneContent value={geneInfo} />}
          </span>
        );
      },
    },

    {
      name: 'description',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab === 'GENES',
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'geneDescription',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Full Name">Gene Full Name</span>,
      headerProps,
      minWidth: 295,
      show: ({ tab }) => tab !== 'GENES',
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'gene_ncbi_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.gene_ncbi_id);
        if (result === '0') return '0';
        return <>{getGeneNCBIid(result)}</>;
      },
    },

    {
      name: 'geneNcbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.geneNcbiID ?? data?.gene_ncbi_id);
        if (result === '0') return '0';
        return renderGeneNCBILink(result);
      },
    },

    {
      name: 'geneNcbiIDs',
      category: 'Gene',
      sortable: true,
      header: <span title="Genes NCBI id">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.geneNcbiIDs);
        if (result === '0') return '0';
        return (
          <>
            {result.map((id, index) => (
              <span key={id}>
                {getGeneNCBIid(String(id))}
                {index < result.length - 1 && ' | '}
              </span>
            ))}
          </>
        );
      },
    },

    {
      name: 'ncbiID',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene NCBI identifier">Gene NCBI Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.ncbiID ?? data?.gene_ncbi_id);
        if (result === '0') return '0';
        return renderGeneNCBILink(result);
      },
    },

    {
      name: 'gene_ensembl_id',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderEnsemblLinks(value),
    },

    {
      name: 'geneEnsemblIDs',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderEnsemblLinks(value),
    },

    {
      name: 'ensembl_ids',
      category: 'Gene',
      sortable: tab === 'VARIANTS' ? false : true,
      header: <span title="Gene ENSEMBL id">Gene ENSEMBL Id</span>,
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderEnsemblLinks(value),
    },

    {
      name: 'geneNcbiType',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      minWidth: 125,
      ...createSelectFilter(dropdownLists.geneNcbiType),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'ncbi_type',
      category: 'Gene',
      sortable: true,
      header: <span title="Gene Type">Gene Type</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.ncbi_type),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneDPI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Pleiotropy Index for the gene">
            DPI<sub>g</sub>
          </span>
          <OpenPopup popupType="showDPI" />
        </>
      ),
      headerProps,
      minWidth: 130,
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneDSI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Disease Specificity Index for the gene">
            DSI<sub>g</sub>
          </span>
          <OpenPopup popupType="showDSI" />
        </>
      ),
      headerProps,
      minWidth: 180,
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'genepLI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Probability of being loss of function intolerant">pLI</span>{' '}
          <OpenPopup popupType="showpLI" />
        </>
      ),
      headerProps,
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'PLI',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Probability of being loss of function intolerant">pLI</span>
          <OpenPopup popupType="showpLI" />
        </>
      ),
      headerProps,
      minWidth: 85,
      render: instance => renderRoundedValues(instance, 'PLI'),
    },

    {
      name: 'geneProteinClassNames',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>{' '}
          <OpenPopup popupType="showProteinClass" />
        </>
      ),
      minWidth: 150,
      headerProps,
      ...createSelectFilter(dropdownLists.geneProteinClassNames),
      render: ({ value }) => renderDefaultValueCols(value),
    },

    {
      name: 'geneProteinStrIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="UniProt Accession">UniProt</span> <OpenPopup popupType="showUniProt" />
        </>
      ),
      headerProps,
      minWidth: 205,
      render: ({ data }) => {
        const { tab } = searchContext();
        let ids = [];
        let values = [];

        if (tab === 'GDA' || tab === 'DDA') {
          ids = data.geneProteinStrIDs;
          values = data.geneProteinStrIDs;
        }

        return <span className="geneModalLink">{renderUniProtLinks(ids, values)}</span>;
      },
    },

    {
      name: 'proteinClassIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="Protein classification according to the DrugTarget Ontology">Protein Class</span>
          <OpenPopup popupType="showProteinClass" />
        </>
      ),
      headerProps,
      minWidth: 200,
      ...createCapitalizeFilter(dropdownLists.proteinClassIDs),
      render: ({ data }) => {
        const proteins = data?.proteins ?? [];
        const uniqueClasses = [...new Set(proteins.flatMap(p => p.proteinClasses?.map(pc => pc.name) ?? []))];
        return uniqueClasses.length ? uniqueClasses.join(' | ') : '-';
      },
    },

    {
      name: 'proteinIDs',
      category: 'Gene',
      sortable: true,
      header: (
        <>
          <span title="UniProt Accession">UniProt</span> <OpenPopup popupType="showUniProt" />
        </>
      ),
      headerProps,
      minWidth: 150,
      render: instance => {
        const proteinIDs = instance?.data?.proteinIDs;

        const result = renderTextColumn(proteinIDs);
        if (result === '-') return '-';

        return proteinIDs.map((uniprot, index) => (
          <span key={uniprot}>
            <a href={`https://www.uniprot.org/uniprot/${uniprot}`} target="_blank" rel="noopener noreferrer">
              {uniprot}
              <i className="fa fa-external-link" aria-hidden="true"></i>
            </a>
            {index < proteinIDs.length - 1 && ' | '}
          </span>
        ));
      },
    },

    {
      name: 'pathwayID',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways IDs">Pathways IDs</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayID'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'pathwayName',
      category: 'Gene',
      sortable: true,
      header: <span title="Pathways Names">Pathways</span>,
      render: instance => renderListsFromObjects(instance, 'pathways', 'pathwayName'),
      headerProps,
      minWidth: 295,
    },

    {
      name: 'numDiseasesAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of diseases associated to the gene">
          N diseases<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems: any[] = [];

        if (tab === 'GENES') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [{ filterParam: 'gene_ncbi_id', filterValue: geneId, name: geneName, id: geneId }];
        } else if (tab === 'GDA') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [
            {
              filterParam: 'gene_ncbi_id',
              filterValue: geneId,
              name: geneName,
              id: geneId,
            },
          ];
        } else {
          return renderNumericColumn(data?.numDiseasesAssociatedToGene);
        }

        if (!data.numDiseasesAssociatedToGene) return renderNumericColumn(data?.numDiseasesAssociatedToGene);

        return renderMappingButton(data, {
          displayValue: renderNumericColumn(data.numDiseasesAssociatedToGene),
          mappingItems,
          columVisibilityModelKey: 'numDiseasesAssociatedToGene',
          tabSegment: 'gda',
          name: 'numDiseasesAssociatedToGene',
          viewSegment: '/summary',
        });
      },
    },

    {
      name: 'numVariantsAssociatedToGene',
      category: 'Gene',
      sortable: true,
      header: (
        <span title="Number of variants associated to the gene">
          N variants<sub>g</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      show: ({ tab, source }) => {
        if (tab !== 'GENES') return true;

        const sourcesWithoutVariants = ['ORPHANET', 'GENCC', 'MGD_HUMAN', 'PSYGENET', 'RGD_HUMAN'];

        return !sourcesWithoutVariants.includes(source ?? '');
      },
      render: ({ data }) => {
        const { tab } = searchContext();
        let mappingItems: any[] = [];

        if (tab === 'GENES') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [{ filterParam: 'gene_ncbi_id', filterValue: geneId, name: geneName, id: geneId }];
        } else if (tab === 'GDA') {
          const geneId = getGeneMappingId(data);
          const geneName = getGeneMappingName(data);
          mappingItems = [
            {
              filterParam: 'gene_ncbi_id',
              filterValue: geneId,
              name: geneName,
              id: geneId,
            },
          ];
        } else {
          return renderNumericColumn(data?.numVariantsAssociatedToGene);
        }

        if (!data.numVariantsAssociatedToGene) return renderNumericColumn(data?.numVariantsAssociatedToGene);

        return renderMappingButton(data, {
          displayValue: renderNumericColumn(data.numVariantsAssociatedToGene),
          mappingItems,
          columVisibilityModelKey: 'numVariantsAssociatedToGene',
          tabSegment: 'vda',
          name: 'numVariantsAssociatedToGene',
          viewSegment: '/summary',
        });
      },
    },
  ];
};
